import React, {useState, useEffect, useCallback, useMemo} from "react";
import {Box, Button, Typography, TextField} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {Modal} from "@buildwitt/shapeshift-common/v2/modal";
import {GridColDef, GridSelectionModel, GridSortModel, GridFilterModel} from "@mui/x-data-grid-pro";
import {AdminTable} from "@/app2/shared/components/AdminTable/AdminTable";
import {AdminTableLanguagesFilterSelect} from "@/components/common/table/AdminTableLanguagesFilterSelect";
import {useSearchLessons, useGetI18nLocales} from "@/app2/shared/data";
import type {LessonSearchResult} from "@/app2/shared/data/gen";
import {convertSecondsToHHMMSS} from "@/utils";
import {gridFilterModelToApiFilters} from "@/app2/pages/TeamProgressPage/utils/filters";
import useCustomDataGrid from "@/app2/shared/hooks/useCustomDataGrid";
import {formatDateForDisplay} from "@/app2/shared/utils/dateManipulation";

export interface IProps {
	currentLessons: number[]; // Already in curriculum
	onClose: () => void;
	onAdd: (selectedLessons: Array<{id: number; title: string; slug: string; publishedAt: Date | null}>) => void;
}

const useStyles = makeStyles()((_theme) => ({
	modal: {
		zIndex: 50,
		"[class*='contentContainer']": {
			padding: "20px",
			maxHeight: "calc(100vh - 200px)",
			width: "1000px"
		}
	},
	container: {
		display: "flex",
		gap: "20px",
		flexDirection: "column"
	},
	header: {
		display: "flex",
		flexDirection: "column",
		gap: "10px"
	},
	subtitle: {
		color: "#F9F9F9B2"
	},
	searchField: {
		marginBottom: "20px"
	},
	selectedCountFooter: {
		color: "grey",
		paddingRight: "15px",
		fontSize: "12px"
	}
}));

export const LessonSearchModal: React.FC<IProps> = ({currentLessons, onClose, onAdd}) => {
	const {classes} = useStyles();
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedRows, setSelectedRows] = useState<Record<number, LessonSearchResult>>({});
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(25);
	const [sortModel, setSortModel] = useState<GridSortModel>([]);
	const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
	const { 
		api: { 
			assignFilters
		} 
	} = useCustomDataGrid<LessonSearchResult>({});

	const selectionModel = useMemo<GridSelectionModel>(
		() => Object.keys(selectedRows).map(Number),
		[selectedRows]
	);

	// Debounced search
	const [debouncedSearch, setDebouncedSearch] = useState("");
	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
		return () => clearTimeout(timer);
	}, [searchTerm]);

	// Reset page when filters, sort, or search change (keep selection intact)
	useEffect(() => {
		setPage(0);
	}, [filterModel, sortModel, debouncedSearch]);

	const filtersApi = gridFilterModelToApiFilters(filterModel);
	const orderBy = sortModel[0]?.field ?? "title";
	const order = sortModel[0]?.sort ?? "asc";

	const {data: lessonsData, isLoading} = useSearchLessons({
		searchTerm: debouncedSearch || undefined,
		extendedAccess: true,
		page: page + 1,
		pageSize,
		orderBy: orderBy === "updatedAt" ? "updatedAt" : orderBy === "publishedAt" ? "publishedAt" : orderBy === "videoDuration" ? "videoDuration" : "title",
		order,
		filters: filtersApi
	});

	const {data: localesData} = useGetI18nLocales();
	const locales = localesData?.data ?? [];

	const apiRows = lessonsData?.data ?? [];
	const totalCount = lessonsData?.totalCount ?? apiRows.length;
	const availableLessons = useMemo(
		() => apiRows.filter((lesson) => !currentLessons.includes(lesson.id)),
		[apiRows, currentLessons]
	);

	const handleSelectionModelChange = useCallback((newSelection: GridSelectionModel) => {
		const visibleIds = new Set(availableLessons.map((r) => r.id));
		setSelectedRows((prev) => {
			const next = { ...prev };
			availableLessons.forEach((row) => {
				if (newSelection.includes(row.id)) {
					next[row.id] = row;
				}
			});
			visibleIds.forEach((id) => {
				if (!newSelection.includes(id)) {
					delete next[id];
				}
			});
			return next;
		});
	}, [availableLessons]);

	const handleFilterModelChange = useCallback((newFilterModel: GridFilterModel) => {
		setFilterModel(newFilterModel);
	}, []);

	const columns: GridColDef<LessonSearchResult>[] = useMemo(
		() => [
			{
				field: "title",
				type: "string",
				headerName: "Title",
				flex: 1,
				minWidth: 240
			},
			{
				field: "videoDuration",
				type: "number" as const,
				headerName: "Duration",
				headerAlign: "left",
				align: "left",
				flex: 1,
				valueGetter: (params) => params.value != null ? convertSecondsToHHMMSS(params.value as number) : ""
			},
			{
				field: "updatedAt",
				type: "date",
				headerName: "Last Modified",
				flex: 1,
				valueGetter: (params) => params.value ? new Date(params.value as string) : null,
				valueFormatter: (params) => formatDateForDisplay(params.value as Date | null)
			},
			{
				field: "publishedAt",
				type: "date",
				headerName: "Published",
				flex: 1,
				valueGetter: (params) => params.value ? new Date(params.value as string) : null,
				valueFormatter: (params) => formatDateForDisplay(params.value as Date | null)
			},
			{
				field: "languages",
				type: "string",
				headerName: "Supported Languages",
				filterable: true,
				sortable: false,
				flex: 1,
				valueGetter: (params) => {
					const codes = params.value as string[] | undefined;
					if (!codes?.length) return "";
					return codes
						.map((code) => (locales as { code?: string; name?: string }[]).find((l) => l.code === code)?.name ?? code)
						.join(", ");
				},
				filterOperators: [
					{
						label: "contains",
						value: "co",
						InputComponent: AdminTableLanguagesFilterSelect,
						getApplyFilterFn: () => null
					}
				]
			},
			{
				field: "authorCohort",
				type: "string",
				headerName: "Shared By",
				filterable: true,
				sortable: false,
				flex: 1,
				valueGetter: (params) => (params.value as LessonSearchResult["authorCohort"])?.title ?? ""
			}
		],
		[locales]
	);

	const handleAdd = () => {
		const selected = Object.values(selectedRows).map((lesson) => ({
			...lesson,
			publishedAt: lesson.publishedAt ? new Date(lesson.publishedAt) : null
		}));
		onAdd(selected);
	};

	const handleExport = () => {
		const headers = ["Title", "Duration", "Last Modified", "Published", "Supported Languages", "Shared By"];
		const langNames = (codes: string[] | undefined) =>
			(codes ?? []).map((code) => (locales as { code?: string; name?: string }[]).find((l) => l.code === code)?.name ?? code).join(", ");
		const rows = availableLessons.map((l) => [
			l.title,
			typeof l.videoDuration === "number" ? convertSecondsToHHMMSS(l.videoDuration) : "",
			formatDateForDisplay(l.updatedAt),
			formatDateForDisplay(l.publishedAt),
			langNames(l.languages),
			l.authorCohort?.title ?? ""
		]);
		const csv = [headers.join(","), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))].join("\n");
		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = "lessons-search-results.csv";
		a.click();
		URL.revokeObjectURL(url);
	};

	return (
		<Modal
			open={true}
			title="Choose Lessons"
			className={classes.modal}
			onClose={onClose}
			footerButtons={[
				<Button key="cancel" onClick={onClose}>
					Cancel
				</Button>,
				selectionModel.length > 0 ? (
					<Typography key="stat" className={classes.selectedCountFooter}>
						Selected {selectionModel.length} lesson{selectionModel.length !== 1 ? "s" : ""}
					</Typography>
				) : null,
				<Button key="add" disabled={selectionModel.length === 0} onClick={handleAdd}>
					Add
				</Button>
			]}
		>
			<Box className={classes.container}>
				<Box className={classes.header}>
					<Typography variant="h6">Choose Lessons</Typography>
					<Typography className={classes.subtitle}>
						Select up to 20 lessons. You&apos;ll be able to arrange and organize your lessons after adding them to your course.
					</Typography>
				</Box>

				<TextField
					className={classes.searchField}
					placeholder="Search lessons by title..."
					label="Search"
					fullWidth
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
				/>

				<AdminTable<LessonSearchResult>
					rows={availableLessons}
					totalCount={totalCount}
					columns={columns.map((e) => assignFilters(e as any))}
					loading={isLoading}
					page={page}
					pageSize={pageSize}
					pageSizeOptions={[10, 25, 50, 100, 200]}
					onPageChange={setPage}
					onPageSizeChange={(newSize) => {
						setPageSize(newSize);
						setPage(0);
					}}
					sortModel={sortModel}
					onSortModelChange={setSortModel}
					filterModel={filterModel}
					onFilterModelChange={handleFilterModelChange}
					checkboxSelection
					selectionModel={selectionModel}
					onSelectionModelChange={handleSelectionModelChange}
					keepNonExistentRowsSelected
					getRowId={(row) => row.id}
					autoHeight
					enableAdvancedFilters={true}
					enableColumnFilter={true}
					onExport={handleExport}
					exportButtonText="Export"
				/>
			</Box>
		</Modal>
	);
};
